import { useEffect, useMemo, useState } from 'react';

import moment from 'moment';

import { database, ref, onValue } from 'util/firebase';
import { buildOffShelfMap } from 'util/offShelf';

/**
 * The dishes a restaurant currently has off the shelf, keyed by dish id.
 *
 * `inactiveMenus/{restaurantId}` is the live node the POS and the server write to when a dish is 86'd
 * or sells out. The online ordering site has always read it (`OnlineMenu`, `CategorySections`); the
 * admin menu screens never did, which is why a dish could be off the menu for customers while the
 * portal still showed it as perfectly normal.
 *
 * Returns an empty map, never null, so callers can index into it unconditionally — including when
 * Firebase is not configured, where `util/firebase` hands back `null` instead of a subscription.
 */
const useOffShelfDishes = (restaurantId) => {
  const [dishIds, setDishIds] = useState(null);
  const today = moment().format('YYYY-MM-DD');

  useEffect(() => {
    if (!restaurantId) {
      setDishIds(null);
      return undefined;
    }

    const dbRef = ref(database, `inactiveMenus/${restaurantId}`);
    if (!dbRef) {
      return undefined;
    }

    const unsubscribe = onValue(dbRef, (snapshot) => {
      const value = snapshot ? snapshot.val() : null;
      setDishIds(value && value.dishIds ? value.dishIds : null);
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [restaurantId]);

  return useMemo(() => buildOffShelfMap(dishIds, today), [dishIds, today]);
};

export default useOffShelfDishes;
